/**
 * Shared, frontend-safe types for the Alice Tooling & Workflow Registry — the
 * single catalog that gives visibility into Alice's tools, connections, prompts,
 * and workflows before more get added (and buried).
 *
 * The Worker is the source of truth for `tools`, `connections`, and `prompts`
 * (it owns the tool definitions + secrets), and returns them from
 * GET /api/copilot/registry as an `AliceRegistryResponse`. The admin view
 * combines that with the frontend-owned catalogs that already live in this
 * package: models (`AI_MODEL_OPTIONS`), suggestions (`ALICE_SUGGESTION_CATALOG`),
 * and workflows (`ALICE_COMPOSER_WORKFLOWS` below).
 *
 * Frontend-safe: metadata + labels only. Connection STATUS is a boolean-ish
 * label; secret values are never included.
 */

import type { AliceComposerTargetType } from './alice';
import type { AliceCommandAlias, AliceWorkflowDefinition } from './alice-workflows';
import type { AliceEffectivePolicy } from './alice-policy';

export type AliceToolAccess = 'read' | 'write';

export type AliceToolCategory =
  | 'tasks'
  | 'organizations'
  | 'contacts'
  | 'leads'
  | 'websites'
  | 'communications'
  | 'billing'
  | 'ai_systems'
  | 'proposals'
  | 'time'
  | 'vault'
  | 'utility';

export interface AliceToolInfo {
  name: string;
  /** Optional user-facing tool name when the runtime name has to differ. */
  displayName?: string;
  description: string;
  category: AliceToolCategory;
  /** Human label for where the data comes from (e.g. "Airtable · ENT_Projects"). */
  dataSource: string;
  access: AliceToolAccess;
  /** Write tools pause for an approval card before anything is written. */
  requiresApproval: boolean;
  /** Connection this tool belongs to (matches AliceConnectionInfo.id). */
  connectorId: string;
  /** Frontend-safe endpoint/action mapping for gateway-style tools. */
  endpoint?: string;
  gatewayAction?: string;
  approvalBehavior?: string;
  exampleInput?: Record<string, unknown>;
}

export type AliceConnectionStatus =
  | 'configured'
  | 'not_configured'
  | 'available'
  | 'planned';

export interface AliceConnectionInfo {
  id: string;
  label: string;
  /** Connection family, e.g. "ai", "data", "email", "logging". */
  kind: string;
  status: AliceConnectionStatus;
  /** Non-secret detail (e.g. a base id, a model label). Never a key. */
  detail?: string;
  /**
   * Whether the operator may switch this connection off for Alice. Core
   * connections (Airtable records, OpenAI) are not toggleable; optional/external
   * ones (Gmail, Stripe, Vapi, raw Airtable) are.
   */
  toggleable?: boolean;
}

export interface AlicePromptInfo {
  id: string;
  label: string;
  description: string;
  /** Optional non-sensitive preview/excerpt of the prompt text. */
  preview?: string;
}

export interface AliceRegistryResponse {
  tools: AliceToolInfo[];
  connections: AliceConnectionInfo[];
  prompts: AlicePromptInfo[];
  /** Multi-step skills/workflows (instruction-wrapped, code-defined). */
  workflows: AliceWorkflowDefinition[];
  /** Slash / phrase / voice triggers that route to workflows. */
  commandAliases: AliceCommandAlias[];
  /** The policy actually in force for this portal (read-only view). */
  effectivePolicy: AliceEffectivePolicy;
  /** ISO timestamp the catalog was generated. */
  generatedAt: string;
}

export interface AliceWorkflowInfo {
  targetType: AliceComposerTargetType;
  label: string;
  description: string;
}

/** Composer/draft workflows Alice can run (frontend-owned). */
export const ALICE_COMPOSER_WORKFLOWS: readonly AliceWorkflowInfo[] = [
  { targetType: 'client_update', label: 'Client update', description: 'Draft a client-safe status update.' },
  { targetType: 'email_reply', label: 'Email reply', description: 'Draft a reply to an email thread.' },
  { targetType: 'task_note', label: 'Task note', description: 'Draft a note to add to a task.' },
  { targetType: 'proposal_section', label: 'Proposal section', description: 'Draft or tighten a proposal section.' },
  { targetType: 'billing_message', label: 'Billing message', description: 'Draft a payment reminder or billing note.' },
  { targetType: 'call_summary', label: 'Call summary', description: 'Summarize a call into notes/follow-ups.' },
  { targetType: 'generic', label: 'Freeform draft', description: 'Open-ended draft session.' },
];

export const ALICE_TOOL_CATEGORY_LABELS: Record<AliceToolCategory, string> = {
  tasks: 'Tasks',
  organizations: 'Organizations',
  contacts: 'Contacts',
  leads: 'Leads',
  websites: 'Websites',
  communications: 'Communications',
  billing: 'Billing',
  ai_systems: 'AI Systems',
  proposals: 'Proposals',
  time: 'Time',
  vault: 'Vault',
  utility: 'Utility',
};
